/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { Field, useFormikContext } from 'formik';
import Button from 'client/component-library/buttons/ButtonAdmin/afsButton';
import { Section } from 'client/modules/common/form/fields/UtilFields';
import { SelectSearchField } from 'client/modules/common/form/fields/SelectSearchField';
import {
  COMPANY_CATEGORY_OPTIONS,
  DATE_FIELD_OPTIONS,
  DEAL_TYPE_OPTIONS,
  GROUP_BY_OPTIONS,
  SORT_BY_DATE_OPTIONS,
} from '../utils/dealConstants';
import { DealFilterOption } from '../apis/GetDealFilterOptions';

// groupBy value -> the noun the limit counts (see claim logic in deal_queue_helpers).
const LIMIT_UNIT_BY_GROUP: Record<number, string> = {
  1: 'companies',
  2: 'articles',
};

interface DealQueueFilterProps {
  handleSubmit: () => void;
  handleClear: () => void;
  queueCount: number;
  isLoading: boolean;
  languageOptions?: DealFilterOption[];
}

export const DealQueueFilter = ({
  handleSubmit,
  handleClear,
  queueCount = 0,
  isLoading,
  languageOptions = [],
}: DealQueueFilterProps) => {
  const rowClass =
    'flex flex-row flex-wrap items-end justify-end gap-x-2 gap-y-1 w-full';

  // When "Keep together" is active, the limit counts groups (companies/articles), not deals -- the
  // claim locks every extraction in each group. Reflect that in the field label so the number is
  // never mistaken for a raw extraction cap.
  const { values, setFieldValue } = useFormikContext<{
    groupBy?: { value?: number };
  }>();
  const groupByValue = values?.groupBy?.value ?? 0;
  const limitUnit = LIMIT_UNIT_BY_GROUP[groupByValue] ?? 'deals';

  return (
    <Section>
      <div className="flex flex-col items-end gap-y-1 w-full">
        {/* Row 1 — filter criteria */}
        <div className={rowClass}>
          <div style={{ width: 108 }}>
            <Field
              excludeDefaultOption
              component={SelectSearchField}
              label="Deal Type"
              placeholder="Deal Type"
              name="dealType"
              options={DEAL_TYPE_OPTIONS}
              data-test="dealTypeSelect"
            />
          </div>

          <div style={{ width: 162 }}>
            <Field
              excludeDefaultOption
              component={SelectSearchField}
              label="Companies"
              placeholder="Companies"
              name="companyCategory"
              options={COMPANY_CATEGORY_OPTIONS}
              data-test="companyCategorySelect"
            />
          </div>

          <div className="relative" style={{ minWidth: 180 }}>
            {/* Sits on the "Languages" label line (right side) so the select box itself stays aligned
                with its row. */}
            <div className="absolute right-0 top-0 flex flex-row gap-x-3">
              <button
                type="button"
                className="text-xs text-blue-600 underline"
                onClick={() => setFieldValue('language', languageOptions)}
                data-test="languageSelectAll"
              >
                Select all
              </button>
              <button
                type="button"
                className="text-xs text-blue-600 underline"
                onClick={() => setFieldValue('language', [])}
                data-test="languageClear"
              >
                Clear
              </button>
            </div>
            <Field
              isMulti
              excludeDefaultOption
              component={SelectSearchField}
              label="Languages"
              placeholder="All languages"
              name="language"
              options={languageOptions}
              data-test="languageSelect"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="idCbiEntity" className="text-xs">
              Company (id_cbi_entity)
            </label>
            <Field
              name="idCbiEntity"
              type="text"
              placeholder="e.g. 12345, 67890"
              className="border rounded text-sm p-1"
              style={{ width: 162 }}
              data-test="idCbiEntityInput"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="idSource" className="text-xs">
              News source (id_source)
            </label>
            <Field
              name="idSource"
              type="text"
              placeholder="e.g. 12, 34"
              className="border rounded text-sm p-1"
              style={{ width: 135 }}
              data-test="idSourceInput"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="amountMin" className="text-xs">
              Amount ≥
            </label>
            <Field
              name="amountMin"
              type="number"
              min="0"
              step="any"
              className="border rounded text-sm p-1"
              style={{ width: 81 }}
              data-test="amountMinInput"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="amountMax" className="text-xs">
              Amount ≤
            </label>
            <Field
              name="amountMax"
              type="number"
              min="0"
              step="any"
              className="border rounded text-sm p-1"
              style={{ width: 81 }}
              data-test="amountMaxInput"
            />
          </div>
        </div>

        {/* Row 2 — date field + range, grouping/sort, limit, actions */}
        <div className={rowClass}>
          <div style={{ width: 135 }}>
            <Field
              excludeDefaultOption
              component={SelectSearchField}
              label="Date field"
              placeholder="Date field"
              name="dateField"
              options={DATE_FIELD_OPTIONS}
              data-test="dateFieldSelect"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="startDate" className="text-xs">
              From
            </label>
            <Field
              name="startDate"
              type="date"
              className="border rounded text-sm p-1"
              data-test="startDateInput"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="endDate" className="text-xs">
              To
            </label>
            <Field
              name="endDate"
              type="date"
              className="border rounded text-sm p-1"
              data-test="endDateInput"
            />
          </div>

          <div style={{ width: 180 }}>
            <Field
              excludeDefaultOption
              component={SelectSearchField}
              label="Sort"
              placeholder="Sort"
              name="sortByDate"
              options={SORT_BY_DATE_OPTIONS}
              data-test="sortByDateSelect"
            />
          </div>

          <div style={{ width: 126 }}>
            <Field
              excludeDefaultOption
              component={SelectSearchField}
              label="Keep together"
              placeholder="Keep together"
              name="groupBy"
              options={GROUP_BY_OPTIONS}
              data-test="groupBySelect"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="limit" className="text-xs">
              Limit ({limitUnit})
            </label>
            <Field
              name="limit"
              type="number"
              min="1"
              className="border rounded text-sm p-1"
              style={{ width: 63 }}
              data-test="limitInput"
            />
          </div>

          <div className="flex flex-col gap-y-0.5 pb-1">
            <label
              className="flex flex-row items-center gap-x-1 text-xs leading-4"
              title="Hides deals that are already linked to a published funding. When unchecked, published deals are still only shown if they differ from the published funding (amount, round, date or investors); ones that fully match are never shown."
            >
              <Field
                name="hidePublished"
                type="checkbox"
                data-test="hidePublishedCheckbox"
              />
              Hide already-published
            </label>
            <label className="flex flex-row items-center gap-x-1 text-xs leading-4">
              <Field
                name="primaryDealsOnly"
                type="checkbox"
                data-test="primaryDealsOnlyCheckbox"
              />
              Primary deals only
            </label>
          </div>
        </div>

        {/* Row 3 — actions */}
        <div className={rowClass}>
          <div style={{ width: 90 }}>
            <Button
              onClick={handleClear}
              data-test="clear-deal-queue-button"
              theme="gray"
              disabled={isLoading}
            >
              Clear Queue
            </Button>
          </div>
          <div style={{ width: 90 }}>
            <Button
              onClick={handleSubmit}
              data-test="fetch-deals-button"
              disabled={queueCount > 0 || isLoading}
            >
              Fetch Deals
            </Button>
          </div>
        </div>
      </div>
    </Section>
  );
};
